import type { Finding, FindingGroup } from './types';
import { CONFIDENCE_RANK } from './types';

export const FINDING_GROUP_ORDER: readonly FindingGroup[] = [
  'risk',
  'opportunity',
  'ontrack',
  'dataReadiness',
] as const;

// Derived, not restated: a second hand-written order would let the tally row and the fallback feed
// order disagree about where a newly added group sits.
const groupRank = (group: FindingGroup): number => FINDING_GROUP_ORDER.indexOf(group);

type IndexedFinding = { finding: Finding; index: number };

/**
 * By the agent's own `decisionRank` — what a reader should act on first. Findings with no
 * `decisionRank` (stored runs written before it existed, or a partial set) fall to the end in the old
 * order — group, then confidence, then the agent's emission order — rather than being interleaved on
 * a rank they never carried. The explicit `index` tie-break preserves emission order without relying
 * on sort stability.
 *
 * Shared so the flat order (`orderFindings`) and the within-group order (`orderedGroups`) can never
 * disagree about which of two findings comes first.
 */
const byDecisionValue = (a: IndexedFinding, b: IndexedFinding): number => {
  const rankA = a.finding.decisionRank;
  const rankB = b.finding.decisionRank;
  if (rankA !== undefined && rankB !== undefined) return rankA - rankB || a.index - b.index;
  if (rankA !== undefined) return -1;
  if (rankB !== undefined) return 1;
  return (
    groupRank(a.finding.group) - groupRank(b.finding.group) ||
    CONFIDENCE_RANK[b.finding.confidence] - CONFIDENCE_RANK[a.finding.confidence] ||
    a.index - b.index
  );
};

const sortByDecisionValue = (findings: Finding[]): Finding[] =>
  findings
    .map((finding, index) => ({ finding, index }))
    .sort(byDecisionValue)
    .map(({ finding }) => finding);

/**
 * The flat feed order, kept for callers that want one undivided list (and its tests). The rendered
 * feed groups by type — see `orderedGroups` — but orders within each group by this same key.
 */
export const orderFindings = (findings: Finding[]): Finding[] => sortByDecisionValue(findings);

export type FindingGroupBucket = {
  group: FindingGroup;
  findings: Finding[];
};

/**
 * Buckets per group, in a fixed severity order (risk → opportunity → ontrack → dataReadiness),
 * omitting any group with no findings. Findings inside a bucket keep the caller's given order — used
 * by the tally line, which only needs counts.
 */
export const groupFindings = (findings: Finding[]): FindingGroupBucket[] =>
  FINDING_GROUP_ORDER.map((group) => ({
    group,
    findings: findings.filter((finding) => finding.group === group),
  })).filter(({ findings: grouped }) => grouped.length > 0);

/**
 * The rendered feed: the same buckets as `groupFindings`, but with each bucket ordered by decision
 * value. Grouping puts a whole category ahead of another (a low-value risk now outranks a high-value
 * opportunity), which is the deliberate trade for a page a reader can scan by type; within a group,
 * consequence still decides the order.
 */
export const orderedGroups = (findings: Finding[]): FindingGroupBucket[] =>
  groupFindings(findings).map(({ group, findings: grouped }) => ({
    group,
    findings: sortByDecisionValue(grouped),
  }));
